import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowRight, AlertCircle, Globe, MapPin, Star, BarChart2, Search, Megaphone, TrendingUp, Target } from 'lucide-react';

const FEED_ITEMS = [
  { id: 1, type: 'kritiek', icon: MapPin, title: 'Google Bedrijfsprofiel niet gekoppeld', desc: 'Uw bedrijf is niet zichtbaar in Google Maps. Dit kost u dagelijks potentiële klanten in uw regio.', why: 'Bedrijven met een geoptimaliseerd Google Bedrijfsprofiel worden tot 70% vaker gevonden.', cat: 'Online Aanwezigheid' },
  { id: 2, type: 'aandacht', icon: Globe, title: 'Facebook profiel niet gekoppeld', desc: 'Uw Facebook-aanwezigheid is nog niet gekoppeld aan het platform. Hierdoor missen we inzichten.', why: 'Social bewijs speelt mee in het vertrouwen van potentiële klanten.', cat: 'Social Media' },
  { id: 3, type: 'kans', icon: Search, title: 'SEO kansen gevonden', desc: 'We hebben 3 nieuwe zoekwoordkansen gevonden die u meer organisch verkeer kunnen opleveren.', why: 'Organisch verkeer is gratis en heeft een hogere koopintentie.', cat: 'SEO' },
  { id: 4, type: 'kritiek', icon: BarChart2, title: 'Google Analytics nog niet gekoppeld', desc: 'Zonder tracking weet u niet hoeveel bezoekers uw website krijgt of waar ze vandaan komen.', why: 'Data-gedreven beslissingen zijn de basis van succesvolle marketing.', cat: 'Web Analytics' },
  { id: 5, type: 'kans', icon: Target, title: 'Geen actieve Google Ads campagne', desc: 'Concurrenten in uw branche adverteren actief op uw kernzoekwoorden.', why: 'Google Ads geeft directe zichtbaarheid voor koopklare bezoekers.', cat: 'Google Ads' },
  { id: 6, type: 'aandacht', icon: Star, title: 'Online reputatie kan verbeterd worden', desc: 'We zien kansen om uw reviewscore te verbeteren en meer positieve reviews te verzamelen.', why: 'Reviews beïnvloeden 93% van de aankoopbeslissingen.', cat: 'Online Reputatie' },
  { id: 7, type: 'kans', icon: MapPin, title: 'Lokale vermeldingen ontbreken', desc: 'Uw bedrijf ontbreekt in 7 lokale bedrijfsgidsen die belangrijk zijn voor uw zichtbaarheid.', why: 'Consistente vermeldingen verbeteren uw lokale SEO en Google-rankings.', cat: 'Lokale Marketing' },
  { id: 8, type: 'aandacht', icon: Globe, title: 'Bedrijfsprofiel is onvolledig', desc: 'Uw profiel mist openingstijden, categorieën en een professionele beschrijving.', why: 'Een volledig profiel trekt 2x meer klanten aan.', cat: 'Bedrijfsprofiel' },
  { id: 9, type: 'kans', icon: TrendingUp, title: 'Contentkansen gevonden', desc: 'We hebben onderwerpen gevonden waarover uw doelgroep actief zoekt maar waarvoor u nog geen content heeft.', why: 'Relevante content trekt organisch verkeer met hoge koopintentie.', cat: 'SEO' },
  { id: 10, type: 'kans', icon: Megaphone, title: 'Offline zichtbaarheid mogelijk in regio', desc: 'In uw regio zijn sponsoringkansen beschikbaar bij lokale sportverenigingen en evenementen.', why: 'Offline branding versterkt uw lokale naamsbekendheid significant.', cat: 'Offline Marketing' },
];

const TYPE_STYLE = {
  kritiek: 'border-red-100 bg-red-50',
  aandacht: 'border-orange-100 bg-orange-50',
  kans: 'border-[#D4A017]/15 bg-[#D4A017]/5',
};
const TYPE_BADGE = {
  kritiek: 'bg-red-100 text-red-700',
  aandacht: 'bg-orange-100 text-orange-700',
  kans: 'bg-[#D4A017]/20 text-[#0f172a]',
};

export default function BrandMonitoring() {
  const { api } = useAuth();
  const [filter, setFilter] = useState('alle');

  const filtered = filter === 'alle' ? FEED_ITEMS : FEED_ITEMS.filter(i => i.type === filter);

  return (
    <div className="max-w-3xl">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-[#0f172a]">Brand Monitoring</h1>
        <p className="text-[#64748b] text-sm mt-1">Actuele aanbevelingen en signalen voor uw bedrijf</p>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-5">
        {[['alle', 'Alles'], ['kritiek', 'Kritiek'], ['aandacht', 'Aandacht'], ['kans', 'Kansen']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${filter === v ? 'bg-[#0f172a] text-white' : 'bg-white text-[#64748b] border border-gray-200 hover:border-[#0f172a]'}`}>
            {l} {v === 'alle' ? `(${FEED_ITEMS.length})` : `(${FEED_ITEMS.filter(i => i.type === v).length})`}
          </button>
        ))}
      </div>

      {/* Feed */}
      <div className="space-y-3">
        {filtered.map(item => {
          const Icon = item.icon;
          return (
            <div key={item.id} className={`rounded-2xl border p-5 ${TYPE_STYLE[item.type]}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Icon className="w-5 h-5 text-[#D4A017]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <h3 className="font-bold text-[#0f172a] text-sm">{item.title}</h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full capitalize ${TYPE_BADGE[item.type]}`}>{item.type}</span>
                    <span className="text-[10px] text-[#94a3b8] bg-white px-2 py-0.5 rounded-full">{item.cat}</span>
                  </div>
                  <p className="text-[#64748b] text-sm mb-2">{item.desc}</p>
                  <div className="bg-white/60 rounded-lg px-3 py-2 mb-3">
                    <p className="text-[#64748b] text-xs"><span className="font-semibold text-[#0f172a]">Waarom belangrijk:</span> {item.why}</p>
                  </div>
                  <div className="flex gap-2">
                    <button className="flex items-center gap-1.5 bg-white text-[#0f172a] font-bold text-xs px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition-all">
                      Zelf doen
                    </button>
                    <Link to="/platform/diensten"
                      className="flex items-center gap-1.5 bg-[#D4A017] text-[#0f172a] font-bold text-xs px-4 py-2 rounded-xl hover:bg-[#B8860B] transition-all">
                      Door IMPERIA AI uitvoeren <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
